import { Dispatch, SetStateAction } from 'react';
import {
  FieldErrors,
  FieldValues,
  Path,
  UseFormTrigger,
} from 'react-hook-form';
import { ZodObject, ZodRawShape, ZodSchema, ZodTypeAny } from 'zod';
import { getSchemaKeys, unwrapZod } from './simple-form-page';

import Step from '@mui/material/Step';
import Stepper from '@mui/material/Stepper';
import Box from '@mui/material/Box';
import StepLabel from '@mui/material/StepLabel';
import { useTheme } from '@mui/material/styles';
import StepContent from '@mui/material/StepContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export interface ActionTypeProps {
  id: string;
  label: string;
}

export interface FormSubSteps {
  id?: string;
  stepIndex: string;
  title: string;
  description?: string;
  hasError: boolean;
}

export interface ActionErrorEntry {
  id: string;
}

export interface StepConfig {
  id?: string;
  label: string;
  schema?: ZodSchema;
  description?: string;
  actionTypes?: ActionTypeProps[];
}

interface SharedFormStepperProps<T extends FieldValues> {
  activeStep: number;
  setActiveStep: (step: number) => void;
  steps: StepConfig[];
  errors: FieldErrors<T>;
  trigger: UseFormTrigger<T> | UseFormTrigger<Partial<T>>;
  stepCompleted: boolean[];
  setStepCompleted: Dispatch<SetStateAction<boolean[]>>;
  handleStepClick?: (index: number) => void;
  handleValidation?: () => boolean;
  subStep?: FormSubSteps[];
}

export function stepHasError(
  schema: ZodObject<ZodRawShape>,
  errors: FieldErrors
): boolean {
  const schemaKeys = schema.shape ? Object.keys(schema.shape) : [];
  if (!schemaKeys || schemaKeys.length <= 0) return false;
  return schemaKeys.some((key) => {
    const schemaValue = schema.shape[key] as ZodTypeAny;
    const unwrappedSchema = unwrapZod(schemaValue);

    if (unwrappedSchema instanceof ZodObject) {
      if (
        errors[key] &&
        stepHasError(
          unwrappedSchema as ZodObject<ZodRawShape>,
          errors[key] as FieldErrors
        )
      ) {
        return true;
      }
    } else {
      if (errors[key]) {
        return true;
      }
    }
    return false;
  });
}

export default function SharedFormStepper<T extends FieldValues>({
  activeStep,
  setActiveStep,
  steps,
  errors,
  trigger,
  stepCompleted,
  setStepCompleted,
  handleStepClick,
  subStep,
}: SharedFormStepperProps<T>) {
  const theme = useTheme();

  async function onStepClick(stepIndex: number) {
    if (stepIndex === activeStep || !stepCompleted[stepIndex]) return;

    if (steps[activeStep].schema) {
      const schema = steps[activeStep].schema;
      const fields = getSchemaKeys(schema as ZodObject<ZodRawShape>);
      const isValid = await trigger(
        fields as readonly Path<T>[] & Path<Partial<T>>[]
      );
      if (!isValid) return;
    }
    setActiveStep(stepIndex);
    setStepCompleted((prev) => {
      const newCompleted = [...prev];
      newCompleted[activeStep] = true;
      return newCompleted;
    });
  }

  return (
    <Box sx={{ flexGrow: 1, pr: 1 }}>
      <Stepper
        nonLinear
        activeStep={activeStep}
        orientation="vertical"
        sx={{
          '& .MuiStepLabel-iconContainer .MuiStepIcon-root': {
            fontSize: 10,
            borderRadius: '50%',
            backgroundColor: theme.palette.colors.neutral[200],
            color: 'transparent',
            opacity: 1,
          },
          '& .MuiStepIcon-root.Mui-completed': {
            backgroundColor: theme.palette.primary.main,
          },
          '& .MuiStepContent-root': {
            pl: 2.5,
            ml: 0.5,
          },
          '& .MuiStepIcon-text': { display: 'none' },
          '& .MuiStepConnector-root': { ml: 0.5 },
          '& .MuiStepConnector-line': { minHeight: 10 },
        }}
      >
        {steps.map((step, index) => {
          const hasError =
            step.schema &&
            stepHasError(step.schema as ZodObject<ZodRawShape>, errors);
          const stepSubStep =
            subStep?.filter((s) => s.stepIndex === step.label) ?? [];
          return (
            <Step
              key={step.label}
              onClick={() =>
                !handleStepClick ? onStepClick(index) : handleStepClick(index)
              }
              completed={stepCompleted[index]}
            >
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': {
                    fontSize: 14,
                    color: hasError
                      ? theme.palette.warning.main
                      : activeStep === index
                      ? theme.palette.text.primary
                      : theme.palette.mode === 'light'
                      ? theme.palette.colors.neutral[600]
                      : theme.palette.colors.neutral[200],
                  },
                  '& .MuiStepLabel-label.Mui-active': {
                    fontWeight: 600,
                    color: hasError
                      ? theme.palette.warning.main
                      : theme.palette.mode === 'light'
                      ? theme.palette.primary.main
                      : theme.palette.semantic.text.info,
                  },
                  '& .MuiStepIcon-root': {
                    backgroundColor: hasError
                      ? theme.palette.warning.main
                      : theme.palette.colors.neutral[200],
                    color: 'transparent',
                    opacity: 1,
                  },
                  '& .MuiStepIcon-root.Mui-active': {
                    backgroundColor: hasError
                      ? theme.palette.warning.main
                      : theme.palette.mode === 'light'
                      ? theme.palette.primary.main
                      : theme.palette.semantic.text.info,
                  },
                  '& .MuiStepIcon-root.Mui-completed': {
                    backgroundColor: hasError
                      ? theme.palette.warning.main
                      : theme.palette.primary.main,
                  },
                  cursor: stepCompleted[index] ? 'pointer' : 'default',
                  pointerEvents: stepCompleted[index] ? 'auto' : 'none',
                }}
                error={hasError && stepCompleted[index]}
              >
                {step.label}
              </StepLabel>
              {stepSubStep && stepSubStep.length > 0 && (
                <StepContent>
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {stepSubStep
                      .slice()
                      .reverse()
                      .map((subDetail) => {
                        const isWarning = subDetail.hasError ?? false;

                        return (
                          <Typography
                            key={subDetail.id || subDetail.title}
                            variant="body2"
                            sx={{
                              color: isWarning
                                ? theme.palette.warning.main
                                : 'text.secondary',
                            }}
                          >
                            {subDetail.title}
                          </Typography>
                        );
                      })}
                  </Stack>
                </StepContent>
              )}
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
}
